import { inject, Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { combineLatest, Observable, pipe } from 'rxjs';
import {
  distinctUntilChanged,
  map,
  mergeMap,
  tap,
  withLatestFrom
} from 'rxjs/operators';
import { PaginatedResponse } from 'src/app/models/paginated-response';
import { ReportService } from '../shared/services/report.service';
import { TransportJobReportRow } from './transport-job-report-row';

type FilterBy = null | 'product' | 'category';

interface State {
  results: TransportJobReportRow[] | null;
  loadingResults: boolean;
  reportGenerated: boolean;
  from: string | null;
  to: string | null;
  assignedTo: number | null;
  pagination: {
    page: number;
    perPage: number;
    sortBy: string;
    sortOrder: string;
    count: number;
  };
  datasource: Observable<TransportJobReportRow[]> | null;
}

const defaultState: State = {
  results: null,
  loadingResults: false,
  reportGenerated: false,
  from: null,
  to: null,
  assignedTo: null,
  pagination: {
    page: 1,
    perPage: 10,
    sortBy: 'created_at',
    sortOrder: 'desc',
    count: 0
  },
  datasource: null
};

@Injectable()
export class TransportJobReportStoreService extends ComponentStore<State> {
  constructor() {
    super(defaultState);
  }

  readonly reportService = inject(ReportService);
  readonly loadingResults$ = this.select(
    ({ loadingResults }) => loadingResults
  );
  readonly results$ = this.select(({ results }) => results);
  readonly pagination$ = this.select(({ pagination }) => pagination);
  readonly filters$ = this.select(({ from, to, assignedTo }) => ({
    from,
    to,
    assignedTo
  }));

  readonly reportGenerated$ = this.select(
    ({ reportGenerated }) => reportGenerated
  );
  readonly datasource$ = this.select(({ datasource }) => datasource);

  readonly setFilters = this.updater<{
    from: string | null;
    to: string | null;
    assignedTo: number | null;
  }>((state, filters) => ({
    ...state,
    ...filters,
    pagination: {
      ...state.pagination,
      page: 0
    }
  }));

  readonly setSort = this.updater<{
    sortBy: string;
    sortOrder: string;
  }>((state, sort) => ({
    ...state,
    pagination: {
      ...state.pagination,
      ...sort,
      page: 0
    }
  }));

  readonly setPage = this.updater<{
    page: number;
    perPage: number;
  }>((state, page) => ({
    ...state,
    pagination: {
      ...state.pagination,
      ...page
    }
  }));

  generateReport(filters: {
    from: string | null;
    to: string | null;
    assignedTo: number | null;
  }) {
    this.setFilters(filters);

    this.patchState({
      reportGenerated: true,
      datasource: combineLatest([
        this.filters$.pipe(
          distinctUntilChanged((previous, current) => {
            return JSON.stringify(previous) === JSON.stringify(current);
          })
        ),
        this.pagination$.pipe(
          distinctUntilChanged((previous, current) => {
            return JSON.stringify(previous) === JSON.stringify(current);
          })
        )
      ]).pipe(
        mergeMap(([filters, pagination]) => {
          return this.reportService
            .getTransportJobReport(filters, pagination)
            .pipe(
              tapResponse(
                (response: PaginatedResponse<TransportJobReportRow>) => {
                  this.patchState((state) => ({
                    pagination: {
                      ...state.pagination,
                      count: response.count
                    }
                  }));
                },
                (err) => console.log(err)
              ),
              map((response) => response.data)
            );
        })
      )
    });
  }
}
